import type { SafetyDashboardRow } from '../../services/reportsApi';
import { formatRiskPatternSummary } from '../incidents/riskPatternWorkflowReviewContext';

export type PortfolioRiskPatternRow = Pick<
  SafetyDashboardRow,
  'workflow_id' | 'risk_type' | 'camera_id' | 'device_id' | 'zone_id' | 'zone_label' | 'area_label'
>;

function normalizeText(value: unknown): string {
  return String(value || '').trim().toLowerCase();
}

export function isProbeWorkflowRow(row: PortfolioRiskPatternRow): boolean {
  const riskType = normalizeText(row.risk_type);
  if (riskType === 'probe') return true;
  const workflowId = String(row.workflow_id || '').trim();
  if (!workflowId) return false;
  const head = workflowId.split('|', 1)[0]?.trim().toLowerCase();
  return head === 'probe';
}

function scopeKey(row: PortfolioRiskPatternRow): string {
  const deviceId = String(row.device_id || '').trim();
  const cameraId = String(row.camera_id || '').trim();
  if (deviceId || cameraId) return `${deviceId}::${cameraId}`;
  return `workflow::${String(row.workflow_id || '').trim()}`;
}

export function dedupeCandidateRiskPatterns<T extends PortfolioRiskPatternRow>(rows: T[]): T[] {
  const grouped = new Map<string, T[]>();
  for (const row of rows) {
    const key = scopeKey(row);
    const bucket = grouped.get(key) || [];
    bucket.push(row);
    grouped.set(key, bucket);
  }

  const kept: T[] = [];
  Array.from(grouped.values()).forEach((group) => {
    const nonProbe = group.filter((row) => !isProbeWorkflowRow(row));
    kept.push(...(nonProbe.length > 0 ? nonProbe : group));
  });
  return kept;
}

export function resolveZoneLabelForRiskPattern(row: PortfolioRiskPatternRow): string | null {
  const zoneLabel = String(row.zone_label || row.area_label || row.zone_id || '').trim();
  return zoneLabel || null;
}

export function buildRiskPatternTitle(row: PortfolioRiskPatternRow): string {
  const label = formatRiskPatternSummary(row);
  const zoneLabel = resolveZoneLabelForRiskPattern(row);
  if (zoneLabel && !normalizeText(zoneLabel).includes('selected area')) {
    return `${label} in ${zoneLabel}`;
  }
  if (row.zone_id && !normalizeText(String(row.zone_id)).includes('selected')) {
    return `${label} in ${String(row.zone_id).trim()}`;
  }
  return label;
}
